// One book's summary inside the Fleet "Books" module face. State-only: no
// links or buttons (the drawer carries the book-page links). Uses the SAME
// per-book query keys/options as BookCard / BookSparkline so the drawer is a
// cache hit. A dormant book (`bookStatus === null`) 404s by design, so it is
// never queried and never renders a figure, a flat-zero line or NaN.
import type { ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { accountApi, convictionApi } from '../../api/client'
import type { Book, BookRole, DrawdownFloorStatus } from '../../types'
import { Sparkline } from '../viz/Sparkline'
import { isDormant, num, signTone, stillLoading, unavailable } from './fleetModuleHooks'
import { fmtPct, fmtSignedUsd, fmtUsd } from './fleetSummaries'
import './BookMiniCard.css'

const DASH = '—'
const POSITION_CAP = 25

const ROLE_LABEL: Record<BookRole, string> = {
  research: 'Research',
  validation: 'Validation',
  live: 'Live',
  book: 'Book',
}

const ROLE_COLOR: Record<BookRole, string> = {
  research: 'var(--series-1)',
  validation: 'var(--series-2)',
  live: 'var(--series-4)',
  book: 'var(--series-1)',
}

interface BookMiniCardProps {
  book: Book | undefined
  role: BookRole
}

type DotTone = 'gain' | 'warn' | 'muted'

export function BookMiniCard({ book, role }: BookMiniCardProps) {
  const queryClient = useQueryClient()
  // Dormant books are not queried (their endpoints 404 by design).
  const queried = book !== undefined && !isDormant(book)

  const accountQ = useQuery({
    queryKey: ['account', book?.id],
    queryFn: () => accountApi.get(book?.id),
    enabled: queried,
    retry: false,
    refetchInterval: 10000,
  })
  const historyQ = useQuery({
    queryKey: ['equity-history', book?.id, '1M'],
    queryFn: () => accountApi.history(book?.id, '1M'),
    enabled: queried,
    retry: false,
    staleTime: 300000,
  })
  const convictionQ = useQuery({
    queryKey: ['conviction-status', book?.id],
    queryFn: () => convictionApi.status(book?.id),
    enabled: queried,
    retry: 1,
    refetchInterval: 30000,
  })

  const label = ROLE_LABEL[role]

  if (book === undefined) {
    return (
      <div className="book-mini">
        <div className="book-mini-role">
          <i className="book-mini-dot book-mini-dot--muted" role="img" aria-label="not yet created" />
          {label}
        </div>
        <div className="book-mini-pnl">not yet created</div>
      </div>
    )
  }

  // Status derivation as BookCard.tsx.
  const status = book.bookStatus
  const dormant = status === null
  const halted = status?.halted === true
  let tone: DotTone = 'gain'
  let statusLabel = 'running'
  if (dormant) {
    tone = 'muted'
    statusLabel = 'dormant'
  } else if (halted) {
    tone = 'warn'
    statusLabel = status.halt_reason ? `halted: ${status.halt_reason}` : 'halted'
  }

  // Drawdown is cache-only: never a new fetch; omitted when absent.
  const cachedDd = queryClient.getQueryData<DrawdownFloorStatus>(['drawdown-floor', book.id])?.drawdown
  const dd = num(cachedDd)

  const heldRaw = convictionQ.data ? (num(convictionQ.data.candidates_by_state?.held) ?? 0) : null
  const held = dormant || heldRaw === null ? DASH : String(heldRaw)
  const kv = `${held}/${POSITION_CAP}${dd !== null && !dormant ? ` · dd ${fmtPct(-dd * 100, 1)}` : ''}`

  const account = accountQ.data
  const equity = num(account?.account_value)
  const dailyPnl = num(account?.daily_pnl)
  const failed = !dormant && unavailable(accountQ)
  const loading = !dormant && stillLoading(accountQ)

  let pnl: ReactNode = null
  let pnlTone = ''
  if (dormant) {
    pnl = 'dormant'
  } else if (failed) {
    pnl = <span className="warn">unavailable</span>
  } else if (!loading && equity !== null && dailyPnl !== null) {
    const base = equity - dailyPnl
    pnl = `${fmtSignedUsd(dailyPnl)} · ${base > 0 ? fmtPct((dailyPnl / base) * 100) : DASH}`
    pnlTone = ` book-mini-pnl--${signTone(dailyPnl)}`
  }

  const series = failed || dormant
    ? []
    : (historyQ.data?.points ?? []).map((p) => num(p.equity)).filter((v): v is number => v !== null)

  return (
    <div className="book-mini">
      <div className="book-mini-role">
        <i className={`book-mini-dot book-mini-dot--${tone}`} role="img" aria-label={statusLabel} />
        {label}
        {halted && !dormant && <span className="warn book-mini-halt">halted</span>}
      </div>
      <div className="book-mini-eq">{equity === null || failed || dormant ? DASH : fmtUsd(equity)}</div>
      <div className={`book-mini-pnl${pnlTone}`}>{pnl}</div>
      <Sparkline values={series} color={ROLE_COLOR[role]} height={24} ariaLabel={`${label.toLowerCase()} equity 1M`} />
      <div className="book-mini-kv">{kv}</div>
    </div>
  )
}
